import { type ChangeEvent, useEffect, useState } from 'react'
import { sendGet, sendPost } from 'hooks/use-fetch'
import { Box, Button, CircularProgress } from '@mui/material'
import DataTable from 'Components/Table/DataTable'
import { type PaginationProps, usePagination } from 'hooks/use-pagination'
import { Column, ColumnType } from 'Components/Table/DataTable/DataTable.model'
import { FormatDateTimeValue } from 'core/Utilities'
import { BannerModal } from 'Components/Modal/BannerModal'
import { TextAreaWithLabel } from 'Components/TextArea'
import { type CommunicationSummary } from 'Pages/CareNetworkManagement/CareNetworkDetail/CommunicationList'
import { type CareNetworkCommunication, NoteType } from 'core/types'
import { useAuth } from 'hooks/use-auth'

export interface EnrollmentNotesProps {
  enrollmentID: number
  isNotesPermission: boolean
  isEditable: boolean
  childID: number

}

export function NewEnrollmentNotes (props: EnrollmentNotesProps): JSX.Element {
  const auth = useAuth()
  const [loaded, setLoaded] = useState(false)
  const [notes, setNotes] = useState<CommunicationSummary[]>([])
  const [showNoteEdit, setShowNoteEdit] = useState(false)
  const [editingNote, setEditingNote] = useState<CareNetworkCommunication | undefined>()
  const [refreshTime, setRefreshTime] = useState(new Date())

  useEffect(() => {
    async function getData (): Promise<void> {
      const request = await sendGet(`/NewEnrollment/EnrollmentNotes?enrollmentId=${props.childID}`, {})
      const referralNotes: CommunicationSummary[] = request.response
      setNotes(referralNotes)

      setLoaded(true)
    }
    void getData()
  }, [refreshTime])

  const showNoteAddBox = (): void => {
    const record: CareNetworkCommunication = {
      id: 0,
      note: '',
      enteredOn: new Date(),
      type: NoteType.Note,
      userID: auth.user?.id ?? 0,
      needMet: null,
      addressedWhatMatteredMost: null,
      reasonNotMet: null,
      reminderDate: null,
      caregiverID: null,
      contactMethodID: null,
      childID: props.childID,
      enrollmentID: props.enrollmentID,
      referralID: null
    }
    setEditingNote(record)
    setShowNoteEdit(true)
  }

  const handleNoteAdd = async (): Promise<void> => {
    if (editingNote !== undefined) {
      const { response } = await sendPost('/Communication/CreateCommunication', editingNote)
      if (response !== undefined) {
        setRefreshTime(new Date())
        setShowNoteEdit(false)
      }
    }
  }

  const handleChange = async (e: ChangeEvent<HTMLInputElement>): Promise<void> => {
    if (editingNote !== undefined) {
      setEditingNote({ ...editingNote, [e.target.name]: e.target.value })
    }
  }

  const getEditNoteContent = (): JSX.Element => {
    if (editingNote === undefined) {
      return <></>
    } else {
      return <Box sx={{ margin: '20px' }}>
        <TextAreaWithLabel
          name="note"
          label="Note"
          data-testid="Note"
          value={editingNote.note}
          onChange={handleChange}
        />
      </Box>
    }
  }
  const noteColumn = new Column('note', 'Note')
  const aboutColumn = new Column('enteredOn', 'About', ColumnType.CUSTOM)
  aboutColumn.customCellGenerator = row => {
    return <div>Added by {row.userName} on {FormatDateTimeValue(row.enteredOn)} </div>
  }

  const columns = [noteColumn, aboutColumn]

  const paginationProps: PaginationProps<CommunicationSummary> = {
    rows: notes,
    initialSize: 5,
    pageSizeSteps: [5, 10]
  }
  const pagination = usePagination(paginationProps)

  if (!loaded) return <CircularProgress role='loading' />

  if (!props.isNotesPermission) {
    return <div>You do not have access to this feature</div>
  }

  return <>
    {props.isEditable && < Box >
      <Button color='secondary' variant='contained' onClick={showNoteAddBox}>Add Note</Button>
    </Box>
    }

    <DataTable
      name='enrollmentNoteTable'
      page={pagination.page}
      data-testid='enrollmentNoteTable'
      hasCheckbox={false}
      columns={columns}
      rows={pagination.internalRows}
      rowClickable={false}
      sortable={false}
      totalRecords={pagination.recordCount}
      onPageChange={pagination.handleChangePage}
      onRowsPerPageChange={pagination.handleChangeRowsPerPage}
      loading={!loaded}
    />

    <BannerModal
      className='Nested Referral'
      maxWidth='sm'
      open={showNoteEdit}
      content={getEditNoteContent()}
      onClose={() => { setShowNoteEdit(false) }}
      onConfirm={handleNoteAdd}
      title='New Enrollment Note'
      cancelButtonText='Cancel'
      confirmButtonText='Save'
    />
  </>
}
